// A pot's drawers (finance-real round 8, slice 9 J41, J42, DD-045, DD-051). ?modal=pot&category=<id>: the pot since its
// first charge, what it paid, passed to clients, holds as BeNerdy's own and has not assigned yet, then a row per month,
// and its repeating expenses with BeNerdy's share of them each month (slice 12). ?modal=pot-month&category=<id>&month=
// YYYY-MM: one month, its charges, its slices by client, and its gap kept as BeNerdy's own (port Q6, the server's
// figure). Every row opens its own drawer in place of this one: a charge or a cost its bill, a month of the ads feed
// its ads drawer, a line out of the pot its client's drawer (finance consolidation S9: the client month is retired). On
// a phone a month row keeps its month, what was paid and what is not assigned (DD-055). Every figure is the server's
// (TD-102).
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { FooterConfirm, useFooterStep } from "@/components/ui/FooterConfirm";
import { MoneyText } from "@/components/ui/MoneyText";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { formatMoney } from "@/utils/currency";
import { FinanceDrawer } from "../components/FinanceDrawer";
import { TotalsFooter } from "../components/TotalsFooter";
import type { ParamPatch } from "../hooks/useFinanceParams";
import { useKeepOwn, useUnkeepOwn } from "../hooks/useExpenses";
import { useSubmitOnce } from "../hooks/useSubmitOnce";
import type { Pot, PotMonth, PotSlice } from "../types";
import { dateLabel, monthLabel } from "../utils/format";
import { notAssignedText } from "./potText";

const heading = "text-[11px] uppercase tracking-wide text-gray-500 mb-1";
const expenseWord = ENTITY_CONFIG.expense.label.toLowerCase();
const expensesWord = ENTITY_CONFIG.expense.plural.toLowerCase();
const categoryWord = ENTITY_CONFIG.expenseCategory.label.toLowerCase();
const clientWord = ENTITY_CONFIG.client.label.toLowerCase();
const clientsWord = ENTITY_CONFIG.client.plural.toLowerCase();
const contractsWord = ENTITY_CONFIG.contract.plural.toLowerCase();
const TO_CLIENTS = `To ${clientsWord}`;
// Month, paid and not assigned on a phone, with to clients and own between them from sm (DD-055: no sideways scroll, R14).
const MONTH_COLS = "grid grid-cols-[minmax(0,1fr)_5.5rem_6rem] sm:grid-cols-[minmax(0,1fr)_5.5rem_5.5rem_5.5rem_6rem] items-center gap-3";
const WIDE = "hidden sm:block text-right";
const money = (minor: number) => formatMoney(minor, "EUR");

/** A pot's or a month's four figures, which add up to paid (A5): the drawer's footer. */
const potTotals = (f: Pick<PotMonth, "paidMinor" | "toClientsMinor" | "ownMinor" | "notAssignedMinor">) => [
  { label: "Paid", amountMinor: f.paidMinor },
  { label: TO_CLIENTS, amountMinor: f.toClientsMinor },
  { label: "BeNerdy's own", amountMinor: f.ownMinor },
  { label: "Not assigned", amountMinor: f.notAssignedMinor },
];

/** The costs out of the pot from before its first charge, which no figure counts (R22). */
function leftOutText(n: number, since: string | null): string {
  const what = n === 1 ? "1 cost" : `${n} costs`;
  const verb = n === 1 ? "comes" : "come";
  const left = n === 1 ? "is left out" : "are left out";
  return since
    ? `${what} from before ${monthLabel(since)} ${verb} out of it and ${left}: no charge is entered for ${n === 1 ? "it" : "them"}.`
    : `${what} ${verb} out of it and ${left} until its first charge is entered.`;
}

interface PotProps {
  pot: Pot;
  onOpen: (patch: ParamPatch) => void;
  onClose: () => void;
}

export function PotDrawer({ pot, onOpen, onClose }: PotProps) {
  return (
    <FinanceDrawer title={pot.label} size="lg" onClose={onClose} footer={<TotalsFooter items={potTotals(pot)} />}>
      <div className="space-y-5 text-sm">
        <div className="space-y-1">
          <Badge tone="blue">Pot</Badge>
          <p className="text-xs text-gray-500">
            {pot.since
              ? `Since ${monthLabel(pot.since)}, its first charge. ${notAssignedText(pot.notAssignedMinor, pot.since)}.`
              : `No charge yet: add one with ${ENTITY_CONFIG.expense.createLabel}, in the ${pot.label} ${categoryWord}.`}
          </p>
          {pot.leftOut > 0 && <p className="text-xs text-gray-400">{leftOutText(pot.leftOut, pot.since)}</p>}
        </div>
        <section>
          <h3 className={heading}>Months</h3>
          <div className={`${MONTH_COLS} py-1 text-[11px] uppercase tracking-wide text-gray-400`}>
            <span>Month</span>
            <span className="text-right">Paid</span>
            <span className={WIDE}>{TO_CLIENTS}</span>
            <span className={WIDE}>Own</span>
            <span className="text-right">Not assigned</span>
          </div>
          <ul className="divide-y divide-gray-100">
            {pot.months.map((m) => (
              <li key={m.month}>
                <button type="button" onClick={() => onOpen({ modal: "pot-month", category: pot.id, month: m.month })} className={`${MONTH_COLS} w-full py-2 text-left hover:bg-gray-50`}>
                  <span className="text-gray-900">{monthLabel(m.month)}</span>
                  <MoneyText amountMinor={m.paidMinor} className="text-right" />
                  <MoneyText amountMinor={m.toClientsMinor} className={WIDE} />
                  <MoneyText amountMinor={m.ownMinor} className={WIDE} />
                  {/* Below zero is clients repaying ahead, never red (DD-054, R15). */}
                  <MoneyText amountMinor={m.notAssignedMinor} className="text-right" />
                </button>
              </li>
            ))}
          </ul>
          <p className="mt-1 text-xs text-gray-400">{`A charge counts in the month it was paid, a ${clientWord}'s cost in its own month: Meta charges after the spend, and a year of hosting is repaid at once.`}</p>
        </section>
        {pot.repeating.length > 0 && (
          <section>
            <h3 className={heading}>{`Repeating ${expensesWord}`}</h3>
            <ul className="divide-y divide-gray-100">
              {pot.repeating.map((r) => (
                <li key={r.templateId}>
                  <button
                    type="button"
                    onClick={() => onOpen(r.due ? { modal: "expense", entry: r.due.billId } : { modal: "expense-item", tpl: r.templateId })}
                    className="flex w-full items-center justify-between gap-3 py-2 text-left hover:bg-gray-50"
                  >
                    <span className="text-gray-900">{r.label}</span>
                    {r.due ? (
                      <Badge tone="amber">{r.due.missing ? `No amount · ${dateLabel(r.due.date)}` : `To pay · ${dateLabel(r.due.date)}`}</Badge>
                    ) : (
                      <span className="text-xs text-gray-500">{r.nextBill ? `Next bill ${dateLabel(r.nextBill)}` : "Stopped"}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
            <p className="mt-1 text-xs text-gray-400">{`BeNerdy's share each month: ${money(pot.monthlyMinor)}, these less what running ${contractsWord} bill out of it, on Cash's health line.`}</p>
          </section>
        )}
        <p className="text-xs text-gray-500">{`A charge is an ${expenseWord} in the ${pot.label} ${categoryWord}. A ${clientWord}'s cost passed along at cost comes out of the pot from its bill, or as it is added.`}</p>
      </div>
    </FinanceDrawer>
  );
}

/** Where a slice opens: its bill, its month of ads, or the drawer of the client its line bills. */
const sliceTarget = (s: PotSlice): ParamPatch =>
  s.source === "bill" ? { modal: "expense", entry: s.id } : s.source === "ads" ? { modal: "ads", entry: s.id } : { modal: "client", client: s.clientId };

const SOURCE_WORDS: Record<PotSlice["source"], string> = { bill: "cost passed along", ads: "ads feed", line: `${ENTITY_CONFIG.contract.label.toLowerCase()} line, at cost` };

function SliceRow({ slice, onOpen }: { slice: PotSlice; onOpen: (patch: ParamPatch) => void }) {
  return (
    <li>
      <button type="button" onClick={() => onOpen(sliceTarget(slice))} className="flex w-full items-center justify-between gap-3 py-2 text-left hover:bg-gray-50">
        <span className="min-w-0">
          <span className="block text-gray-900 truncate">{slice.label}</span>
          <span className="block text-xs text-gray-500">{SOURCE_WORDS[slice.source]}</span>
        </span>
        <MoneyText amountMinor={slice.amountMinor} className="shrink-0" />
      </button>
    </li>
  );
}

/** What the month's not assigned means: waiting for a cost, repaid ahead, or none. */
function notAssignedNote(m: PotMonth): string {
  if (m.notAssignedMinor > 0) {
    return `Not assigned: paid this month and not yet matched by a ${clientWord}'s cost or BeNerdy's own. A cost tagged later, or spend that lands next month, brings it down.`;
  }
  if (m.notAssignedMinor < 0) return `Below zero: ${clientsWord}' costs this month came to more than its charges, which landed in another month.`;
  return "Every euro paid this month is assigned.";
}

interface MonthProps {
  pot: Pot;
  month: PotMonth;
  onOpen: (patch: ParamPatch) => void;
  onClose: () => void;
}

export function PotMonthDrawer({ pot, month, onOpen, onClose }: MonthProps) {
  const keep = useKeepOwn();
  const unkeep = useUnkeepOwn();
  const send = useSubmitOnce();
  const footerStep = useFooterStep<"unkeep">(null);
  const pending = keep.isPending || unkeep.isPending;
  const at = { potId: pot.id, month: month.month };
  const clients = Array.from(new Set(month.slices.filter((s) => !s.own).map((s) => s.clientName)));
  const own = month.slices.filter((s) => s.own);
  // What was kept is a saved record in the drawer: its click asks here, never an inline button (DD-045, R10).
  const footer =
    footerStep.step === "unkeep" && month.keptMinor > 0 ? (
      <FooterConfirm
        confirmLabel="Stop keeping"
        onConfirm={() => send(() => unkeep.mutateAsync(at), footerStep.leave, footerStep.leave)}
        onKeep={footerStep.leave}
        pending={pending}
        loading={unkeep.isPending}
        stacked
      >
        {`Stop keeping ${money(month.keptMinor)} as BeNerdy's own? It reads not assigned again.`}
      </FooterConfirm>
    ) : (
      <TotalsFooter items={potTotals(month)} />
    );
  return (
    <FinanceDrawer title={`${pot.label} · ${monthLabel(month.month)}`} size="lg" onClose={onClose} footer={footer}>
      <div className="space-y-5 text-sm">
        <section>
          <h3 className={heading}>Charges paid</h3>
          {month.charges.length === 0 ? (
            <p className="text-gray-400">No charge this month.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {month.charges.map((c) => (
                <li key={c.billId}>
                  <button type="button" onClick={() => onOpen({ modal: "expense", entry: c.billId })} className="flex w-full items-center justify-between gap-3 py-2 text-left hover:bg-gray-50">
                    <span className="min-w-0">
                      <span className="block text-gray-900 truncate">{c.label}</span>
                      <span className="block text-xs text-gray-500">{c.paidOn ? `Paid ${dateLabel(c.paidOn)}` : `Expected ${dateLabel(c.date)}`}</span>
                    </span>
                    <MoneyText amountMinor={c.amountMinor} muted={!c.paidOn} className="shrink-0" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
        <section>
          <h3 className={heading}>{`${TO_CLIENTS}, at cost`}</h3>
          {clients.length === 0 && <p className="text-gray-400">{`No ${clientWord} cost comes out of it this month.`}</p>}
          {clients.map((name) => (
            <div key={name} className="mb-2">
              <div className="text-xs font-medium text-gray-700">{name}</div>
              <ul className="divide-y divide-gray-100">
                {month.slices.filter((s) => !s.own && s.clientName === name).map((s) => <SliceRow key={s.id} slice={s} onOpen={onOpen} />)}
              </ul>
            </div>
          ))}
        </section>
        {(own.length > 0 || month.keptMinor > 0) && (
          <section>
            <h3 className={heading}>BeNerdy's own</h3>
            <ul className="divide-y divide-gray-100">
              {own.map((s) => <SliceRow key={s.id} slice={s} onOpen={onOpen} />)}
              {month.keptMinor > 0 && (
                <li>
                  {/* The row stays mounted while the footer asks, so focus comes back to it through a ref, not autoFocus. */}
                  <button
                    type="button"
                    ref={footerStep.backRef("unkeep")}
                    onClick={() => footerStep.ask("unkeep")}
                    disabled={pending}
                    className="flex w-full items-center justify-between gap-3 py-2 text-left hover:bg-gray-50"
                  >
                    <span className="min-w-0">
                      <span className="block text-gray-900 truncate">Kept as BeNerdy's own</span>
                      <span className="block text-xs text-gray-500">{`by hand: no ${clientWord}'s cost will match it`}</span>
                    </span>
                    <MoneyText amountMinor={month.keptMinor} className="shrink-0" />
                  </button>
                </li>
              )}
            </ul>
          </section>
        )}
        <div className="space-y-2">
          <p className="text-xs text-gray-500">{notAssignedNote(month)}</p>
          {month.keepableMinor > 0 && (
            <>
              <Button size="sm" variant="secondary" onClick={() => send(() => keep.mutateAsync(at))} disabled={pending} isLoading={keep.isPending}>
                {`Keep as BeNerdy's own · ${money(month.keepableMinor)}`}
              </Button>
              <p className="text-xs text-gray-400">
                {month.keepableMinor < month.notAssignedMinor
                  ? "The pot's other months repaid the rest ahead, so this keeps no more than the pot lacks."
                  : `For a gap no ${clientWord}'s cost will match, as BeNerdy's own spend no feed brings. It moves no cash, and Stop keeping is on its line.`}
              </p>
            </>
          )}
        </div>
      </div>
    </FinanceDrawer>
  );
}
